/**
 * Custom hook for fetching AI-generated trend insights
 */

import { useState, useCallback } from 'react';
import { apiClient, handleApiError } from '@/api/apiClient';
import type { TrendInsightsResponse } from '@/types/scalingUp';

interface UseTrendInsightsResult {
  data: TrendInsightsResponse | null;
  loading: boolean;
  error: string | null;
  fetchInsights: (forceRefresh?: boolean) => Promise<void>;
}

export function useTrendInsights(): UseTrendInsightsResult {
  const [data, setData] = useState<TrendInsightsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const url = forceRefresh
        ? '/scaling-up/trends/insights?refresh=true'
        : '/scaling-up/trends/insights';
      const response = await apiClient.get<{ data: TrendInsightsResponse }>(url);
      setData(response.data.data);
    } catch (err) {
      try {
        handleApiError(err, 'Failed to load trend insights');
      } catch (handled) {
        setError(
          handled instanceof Error ? handled.message : 'Failed to load insights'
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchInsights };
}
